import { defineStore } from 'pinia'
import { getUserSettings, saveUserSettingsLocal } from '@/utils/indexedDb'
import { ref, computed, toRaw } from 'vue'
import log from '@/utils/useLogger'
import type {
  ZonePreference,
  AnnotationPreference,
  DockPreference,
  MapEntityPreference,
} from '@/types/zones.types'
import type { EdgeTxControlMode } from '@/constants/edgetx'
import type { MediaGroupBy } from '@/utils/media'

export type ControllerType = 'Thrust Master' | 'XBox' | 'Keyboard' | 'Dji Fpv' | 'EdgeTX'

type Tiles3dMode = 'none' | 'photorealistic' | 'photorealistic-with-labels'
export const UserSettings = defineStore(
  'user-settings',
  () => {
    const settings = ref<Record<string, any>>({
      // default settings
      natZoneVisible: false,
      natZoneOpacity: 0.5,
      mapStyle: 'google-maps',
      tiles3dMode: 'none' as Tiles3dMode,
      codeHistory: [],
      pairingHistory: [],
      zonePreferences: {} as ZonePreference,
      annotationPreferences: {} as AnnotationPreference,
      dockPreferences: {} as DockPreference,
      mapEntityPreferences: {} as MapEntityPreference,
      selectedMediaDevice: '',
      // Media gallery view preferences (FLY3D-1042). Not filters — they
      // survive "Clear filters".
      mediaGroupBy: 'date' as MediaGroupBy,
      mediaViewMode: 'grid' as 'grid' | 'hover',
      compassPosition: { x: 400, y: 100 },
      selectedController: 'Thrust Master' as ControllerType,
      edgeTxControlMode: 'SEMANTIC' as EdgeTxControlMode,
    })
    const version = ref(1)

    // actions
    const loadLocalSettings = async (userId: string) => {
      try {
        if (!userId) throw new Error('User ID is required')
        const res = await getUserSettings(userId)
        if (res?.settings) {
          // Merge loaded settings over defaults so new keys always have a fallback
          settings.value = { ...settings.value, ...res.settings }
          version.value = res.version
        }
      } catch (err: any) {
        log.error('Failed to load settings', err)
      }
    }

    /**
     * Optimistic update: updates store immediately, persists to IndexedDB.
     * Version is NOT incremented locally - backend handles version control.
     * Reverts on failure.
     */
    const updateSettings = async (
      userId: string,
      newSettings: Record<string, any>,
    ): Promise<boolean> => {
      const oldSettings = { ...toRaw(settings.value) }

      // Optimistic update (keep same version - backend increments on sync)
      settings.value = { ...settings.value, ...newSettings }

      try {
        // Convert to plain object for IndexedDB (removes Vue reactive proxies)
        const plainSettings = JSON.parse(JSON.stringify(toRaw(settings.value)))
        await saveUserSettingsLocal(userId, plainSettings, version.value, new Date().toISOString())
        return true
      } catch (err: any) {
        // Revert on failure
        log.error('Failed to save settings locally, reverting', err)
        settings.value = oldSettings
        return false
      }
    }

    /**
     * Update version from cloud response
     */
    const setVersion = (newVersion: number) => {
      version.value = newVersion
    }

    const selectedController = computed<ControllerType>({
      get: () => (settings.value.selectedController as ControllerType) || 'Thrust Master',
      set: (val: ControllerType) => { settings.value.selectedController = val },
    })

    const edgeTxControlMode = computed<EdgeTxControlMode>({
      get: () => (settings.value.edgeTxControlMode as EdgeTxControlMode) || 'SEMANTIC',
      set: (val: EdgeTxControlMode) => {
        settings.value.edgeTxControlMode = val
      },
    })

    return {
      settings,
      version,
      selectedController,
      edgeTxControlMode,
      loadLocalSettings,
      updateSettings,
      setVersion,
    }
  },
  {
    persist: {
      storage: sessionStorage,
    },
  },
)
